import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import ValidationErrorMessage from './ValidationErrorMessage';
import { createRegistrationSettings } from '../utils/utils';

/**
 * Component for rendering text inputs using react hook form.
 *
 * @params see propTypes
 * @returns {JSX.Element}
 * @component
 */
const TextInput = ({ name, label, validation, defaultValue, customValidation, children }) => {
  /**
   * Deconstructs react hook form methods from parent form.
   */
  const {
    register,
    formState: { errors }
  } = useFormContext();

  /**
   * create Settings used in react hook form register function.
   * @type {{}}
   */
  const settings = createRegistrationSettings(label, defaultValue, validation, customValidation);

  settings.setValueAs = value => value.trim();
  
  return (
    <div className="d-flex flex-column">
      <label
        htmlFor={name}
        className="mt-8 fw-normal">
        {label} {validation?.IsRequired ? '' : '(optional)'}
      </label>
      
      {
        errors[name] &&
        <ValidationErrorMessage error={errors[name]}/>
      }
      
      {
        name === 'POSTALCODE_LOOKUP' ?
          <>
            <p className="mt-2">
              We will use this to look up your address
            </p>
            <div className="mt-2">
              <div className="c-postcode-input-wrapper d-flex flex-wrap">
                <input
                  id={name}
                  className={`c-postcode-input align-self-start border-1 border-${errors[name] ? 'rhubarb-triangle' : 'blue-anchor'} me-4 py-4 px-2`}
                  type="text"
                  {...register(name, settings)} />
                {children}
              </div>
            </div>
          </> 
          :
          <>
            <input
              id={name}
              className={`border-1 border-${errors[name] ? 'rhubarb-triangle' : 'blue-anchor'} mt-2 py-3 px-2 w-100`}
              type="text"
              {...register(name, settings)} />
            {children}
          </>
      }
    </div>
  );
};

TextInput.propTypes = {
  /**
   * The input name
   */
  name: PropTypes.string.isRequired,
  /**
   * The label text
   */
  label: PropTypes.string.isRequired,
  /**
   * A default value
   */
  defaultValue: PropTypes.any,
  /**
   * A validation object provided by the settings api compatible with react hook form.
   */
  validation: PropTypes.object,
  /**
   * A validation object declared on the component compatible with react hook form.
   */
  customValidation: PropTypes.object,
  /**
   * Any children to be rendered.
   */
  children: PropTypes.element
};

export default TextInput;
